import moment from 'moment';
import {WorkshopSiteVisitModel} from '../../model/workshop/workshopSiteVisitModel';
import {SelectedDate} from '../calendar/griha-calendar/griha-calendar.component';
import {
  WorkshopStatus,
  normalizeWorkshopScheduleStatus,
} from '../../model/workshop.status.enum';

export class AppUtils {

  /**
   * Date shown in workshop/site-visit headers. When status is DONE without a
   * scheduled date, falls back to `updated_at` (completion timestamp).
   */
  static resolveWorkshopSiteVisitDisplayDate(
    row: Pick<
      WorkshopSiteVisitModel,
      'confirm_date' | 'proposed_date' | 'selected_days' | 'updated_at' | 'status'
    > | null | undefined,
  ): string | null {
    if (!row) {
      return null;
    }
    const confirm = row.confirm_date?.trim();
    if (confirm) {
      return confirm;
    }
    const proposed = row.proposed_date?.trim();
    if (proposed) {
      return proposed;
    }
    const days = row.selected_days;
    if (Array.isArray(days) && days.length > 0) {
      const first = days[0]?.trim();
      if (first) {
        return first;
      }
    }
    if (
      String(row.status || '').replace(/\s+/g, '_').toUpperCase() ===
        WorkshopStatus.WORKSHOP_STATUS_DONE &&
      row.updated_at?.trim()
    ) {
      return row.updated_at.trim();
    }
    return null;
  }

  static isValidEmail(emailString: string): boolean {
    // Define a regular expression pattern for a valid email address
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Split the input string by commas or semicolons
    // const emails = emailString.split(/[;,]/);

    // const validEmails = [];

    // Iterate through the split strings and validate each part
    // emails.forEach((email) => {
    //   const trimmedEmail = email.trim();
    //   if (emailPattern.test(trimmedEmail)) {
    //     validEmails.push(trimmedEmail);
    //   }
    // });

    return emailPattern.test(emailString);
  }

  static isValidPhone(phone: string): boolean {
    const phonePattern = /^(\+91[0-9]{10}|\d{10})$/;
    return phonePattern.test(phone);
  }

  static isValidAttendees(participants: string[]) {
    let isValidEmail = true;
    if (participants.length === 0) {
      isValidEmail = false;
    } else {
      isValidEmail = participants.every(email => this.isValidEmail(email));
    }
    return isValidEmail;
  }

  static buildDateRangeAndRemainingDays = (workshop: WorkshopSiteVisitModel) : any => {
    console.log(workshop);
    const days = workshop?.selected_days || [];
    const startDay = days[0];
    const endDay = days[4] ?? days[days.length - 1];
    if (!startDay || !endDay) {
      return { workshopWeek: '(Date range unavailable)', remainingDays: 0 };
    }
    const momentStartDate = moment(startDay, 'YYYY-MM-DD');
    const momentEndDate = moment(endDay, 'YYYY-MM-DD');
    const startMonthAndDate = momentStartDate.format('MMM DD');
    const endMonthAndDate = momentEndDate.format('MMM DD');
    const year = momentStartDate.format('YYYY');
    const  workshopWeek = `(${startMonthAndDate} - ${endMonthAndDate}, ${year})`;
    const today = moment().format("YYYY-MM-DD");
    const remainingDays = momentEndDate.diff(today, 'days');
    return {workshopWeek : workshopWeek, remainingDays: remainingDays}
  }

  static clientRequestedWeek = (workshop: WorkshopSiteVisitModel) : string => {
    const startDay = workshop.selected_days[0];
    const endDay = workshop.selected_days[4];
    const momentStartDate = moment(startDay, 'YYYY-MM-DD');
    const momentEndDate = moment(endDay, 'YYYY-MM-DD');
    const startMonthAndDate = momentStartDate.format('MMM DD');
    const endMonthAndDate = momentEndDate.format('MMM DD');
    const year = momentStartDate.format('YYYY');
    return`(${startMonthAndDate} - ${endMonthAndDate}, ${year})`;
  }

  static buildNormalDateRangeAndRemainingDays = (workshop: WorkshopSiteVisitModel) : any => {
    const startDay = workshop.selected_days[0];
    const proposalDate = moment(startDay);
    return AppUtils.getWorkshopWeekAndRemainingDays(proposalDate)
  }

  static buildProposalDateRangeAndRemainingDays = (workshop: WorkshopSiteVisitModel) : any => {
    const proposalDate = moment(workshop.proposed_date);
    return AppUtils.getWorkshopWeekAndRemainingDays(proposalDate)
  }

  static buildConfirmDateRangeAndRemainingDays = (workshop: WorkshopSiteVisitModel) : any => {
    const confirmDate = moment(workshop.confirm_date);
    return AppUtils.getWorkshopWeekAndRemainingDays(confirmDate);
  }

  private static getWorkshopWeekAndRemainingDays(date: any) {
    const today = moment().format("MMM DD, YYYY");
    const  workshopWeek = moment(date).format('MMM DD, YYYY');
    const remainingDays = moment(date).diff(today, 'days');
    return {workshopWeek : workshopWeek, remainingDays: remainingDays}
  }

  static getStartAndEndDateInMoment = (workshop: WorkshopSiteVisitModel) : any => {
    const startDay = workshop.selected_days[0];
    const endDay = workshop.selected_days[4];
    const momentStartDate = moment(startDay, 'YYYY-MM-DD');
    const momentEndDate = moment(endDay, 'YYYY-MM-DD');
    return {startDate : momentStartDate, endDate: momentEndDate}
  }

  static getSelectedDateFromWorkshopOrSiteVisit = (workshop: WorkshopSiteVisitModel) : any => {
    const selectedDate = new SelectedDate();
    const startDay = workshop.selected_days[0];
    const endDay = workshop.selected_days[4];
    const momentStartDate = moment(startDay, 'YYYY-MM-DD');
    const momentEndDate = moment(endDay, 'YYYY-MM-DD');
    selectedDate.selectedDate = `${startDay} To ${endDay}`;
    selectedDate.selectedStartWeek = momentStartDate;
    selectedDate.selectedEndWeek = momentEndDate;
    return selectedDate;
  }

  public static getSelectedDateOnCreation = (selectedDates: string[]): SelectedDate  => {
    const selectedDate = new SelectedDate();
    const startDay = selectedDates[0];
    const endDay = selectedDates[4];
    const momentStartDate = moment(startDay, 'YYYY-MM-DD');
    const momentEndDate = moment(endDay, 'YYYY-MM-DD');
    selectedDate.selectedDate = `${startDay} To ${endDay}`;
    selectedDate.selectedStartWeek = momentStartDate;
    selectedDate.selectedEndWeek = momentEndDate;
    return selectedDate;
  }

  public static removeFirst<T>(array: T[], toRemove: T): void {
    const index = array.indexOf(toRemove);
    if (index !== -1) {
      array.splice(index, 1);
    }
  }

  public static stringToMMDDYYY = (dateString: string):string => {
    const momentDate =  moment(dateString, 'YYYY-MM-DD');
    return momentDate.format('MMM DD YYYY');
  }


  public static refreshUi = (siteVisitOrWorkshop: WorkshopSiteVisitModel) => {
    console.log(`Available project workshop ${siteVisitOrWorkshop}`);
    const result = AppUtils.buildDateRangeAndRemainingDays(siteVisitOrWorkshop);
    const confirmedProposedDate = AppUtils.buildProposalDateRangeAndRemainingDays(siteVisitOrWorkshop);
    const confirmedDate = AppUtils.buildConfirmDateRangeAndRemainingDays(siteVisitOrWorkshop);
    const normalizedStatus = normalizeWorkshopScheduleStatus(
      siteVisitOrWorkshop?.status,
    );
    console.log(`Workshop status :: ${siteVisitOrWorkshop.status} → ${normalizedStatus}`);
    let txtActionButton = '';
    let status = '';
    let canReschedule = false;
    switch (normalizedStatus) {
      case WorkshopStatus.WORKSHOP_STATUS_PROPOSED:
        txtActionButton = 'Accept';
        status = 'Date proposed by admin';
        canReschedule = true;
        result.workshopWeek = confirmedProposedDate.workshopWeek;
        result.remainingDays = confirmedProposedDate.remainingDays;
        break;
      case WorkshopStatus.WORKSHOP_STATUS_FEE_APPROVAL_PENDING:
        txtActionButton = '';
        status = 'Pending fee approval';
        canReschedule = false;
        break;
      case WorkshopStatus.WORKSHOP_STATUS_PENDING_APPROVAL:
        txtActionButton = '';
        status = 'Pending approval';
        canReschedule = false;
        break;
      case WorkshopStatus.WORKSHOP_STATUS_PENDING_CONFIRMATION:
        txtActionButton = '';
        status = 'Pending confirmation';
        canReschedule = false;
        if (siteVisitOrWorkshop.proposed_date) {
          result.workshopWeek = confirmedProposedDate.workshopWeek;
          result.remainingDays = confirmedProposedDate.remainingDays;
        }
        break;
      case WorkshopStatus.WORKSHOP_STATUS_CONFIRMED:
        txtActionButton = '';
        status = 'Confirmed';
        result.workshopWeek = confirmedDate.workshopWeek;
        result.remainingDays = confirmedDate.remainingDays;
        canReschedule = false;
        break;
      case WorkshopStatus.WORKSHOP_STATUS_DONE:
        txtActionButton = '';
        status = 'Done';
        result.workshopWeek = confirmedDate.workshopWeek;
        result.remainingDays = confirmedDate.remainingDays;
        canReschedule = false;
        break;
      default:
        txtActionButton = '';
        status = normalizedStatus
          ? normalizedStatus.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
          : 'To be scheduled';
        canReschedule = false;
        break;
    }

    return {
      res: result,
      txtActionButton: txtActionButton,
      canReschedule: canReschedule,
      status: status,
      isConfirmed:
        normalizedStatus === WorkshopStatus.WORKSHOP_STATUS_CONFIRMED ||
        normalizedStatus === WorkshopStatus.WORKSHOP_STATUS_DONE,
    };
  }


}
